import { Box } from '@mui/system';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Spinner } from 'src/components/spinner';
import { useIsUserAuthenticated } from 'src/lib/hooks/use-is-user-authenticated';
import { selectUserLoggedIn } from 'src/store/slices/user-slice';
import {
    setDualOccupantData,
    setNavigationState,
    setVitaRefStorage,
} from 'src/utils/local-storage-versioning';
import { DualOccupantLoginForm } from 'src/features/dual-occupant-login/dual-occupant-login-form';
import { StepList } from 'src/features/step-list';
import { StepEnum } from 'src/features/step-list/constants';
import { useUpdateDualOccupantDetails } from 'src/lib/hooks/use-post-update-dual-occupant-details';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { PathNames } from 'src/shared/enum/PathNames';
import { setRoomData, setBuildingData } from 'src/store/slices/booking-slice';
import { DualOccupant } from 'src/utils/types/DualOccupant';
import { useGetAmendData } from 'src/lib/hooks/use-get-amend-data';
import { setQueryData } from 'src/store/slices/dual-occupant-slice';
import { SignAgreementsDto } from 'src/api/basketService';
import { routes } from 'src/constants/routes';

import { AgreementsPage } from '../agreements';
import { OccupantDetailsForm } from '../occupant-details/occupant-details-form';
import { FailurePage } from '../failure';

import { styles } from './styles';

const NO_OP_FN = () => {};
export const AmendBookingDualOccupantWrapper: FC = () => {
    const userLoggedIn = useSelector(selectUserLoggedIn);
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [activeStep, setActiveStep] = useState<StepEnum>(StepEnum.YourDetails);
    const {
        data,
        error: roomFetchingError,
        isPending,
    } = useGetAmendData(searchParams.get('ref') ?? '');
    const { mutateAsync, isPending: isUpdatingAdditionalOccupant } = useUpdateDualOccupantDetails();

    const { error, isFetching } = useIsUserAuthenticated(!userLoggedIn);
    const navigate = useNavigate();

    useEffect(() => {
        if (isFetching) return;

        const vitaRef = searchParams.get('ref');
        const user = searchParams.get('u');
        const pin = searchParams.get('pin');

        if (!vitaRef || !user || !pin)
            throw new Error(ErrorMessage.DUAL_OCCUPANT_INSUFFICIENT_METADATA);

        // The user's email is sent as a base64 string in the query so we need to decode
        const userEmail = atob(user);
        dispatch(setQueryData({ vitaRef, auth0Pin: pin, userEmail }));

        setDualOccupantData(new DualOccupant({ email: user, pin, vitaRef }));
    }, [isFetching]);

    const vitaRef = searchParams.get('ref');

    if (error || roomFetchingError || !vitaRef) {
        return <FailurePage />;
    }

    setVitaRefStorage(vitaRef);

    if (isFetching) {
        return (
            <Box css={styles.layout}>
                <Box css={styles.leftSide}>
                    <Spinner />
                </Box>
            </Box>
        );
    }

    const steps = [StepEnum.YourDetails, StepEnum.Agreements];

    const showAgreements = () => setActiveStep(StepEnum.Agreements);
    const hideAgreements = () => setActiveStep(StepEnum.YourDetails);

    const submitAgreements = async (agreementsData: SignAgreementsDto) => {
        if (!agreementsData.booking_terms_signed || !agreementsData.licence_agreement_signed) {
            throw new Error(ErrorMessage.AGREEMENTS_NOT_SIGNED);
        }

        if (!vitaRef) throw new Error(ErrorMessage.NO_VITA_REF);

        await mutateAsync(vitaRef);

        navigate(routes.success.index);
    };

    if (userLoggedIn && !isPending && data) {
        dispatch(setRoomData(data.room));
        dispatch(setBuildingData(data.building));
    }

    return (
        <Box css={styles.layout}>
            <Box css={styles.leftSide}>
                <StepList activeStep={activeStep} steps={steps} />

                {activeStep === StepEnum.YourDetails &&
                    (userLoggedIn ? (
                        <OccupantDetailsForm
                            data={data ?? null}
                            loading={isPending}
                            userFormProps={{
                                onFormSubmit: showAgreements,
                                showDetailsForm: false,
                                handleShowDetailsForm: NO_OP_FN,
                                handleHideDetailsForm: NO_OP_FN,
                                hasRoommates: false,
                                handleUpdateNavigationStorage: () => {
                                    setNavigationState(
                                        PathNames.OCCUPANT_DETAILS,
                                        StepEnum.Agreements
                                    );
                                },
                            }}
                        />
                    ) : (
                        <DualOccupantLoginForm />
                    ))}

                {activeStep === StepEnum.Agreements && (
                    <AgreementsPage
                        hideBackButton={false}
                        onGoBack={hideAgreements}
                        hasRoommates={false}
                        onSubmit={submitAgreements}
                        loading={isUpdatingAdditionalOccupant}
                        error={null}
                        adminFee={false}
                    />
                )}
            </Box>
        </Box>
    );
};
